/**
 * NonWorkingDayFormModal — okno dodawania / edycji dnia wolnego (widok kalendarza).
 *
 * Pola wpisu (data, „Co roku", nazwa) i walidacja jak w liście. Montowane tylko na czas otwarcia
 * (rodzic daje nowy `key` przy każdym otwarciu), więc stan formularza startuje świeżo.
 *
 * Cel okna (`target`) jest ZAMROŻONY przez rodzica przy otwarciu i nie zależy od tego, czy wpis
 * nadal jest na liście: gdy edytowany wpis zniknie (np. usunięty przez kogoś innego, odświeżenie
 * listy), okno zostaje w trybie edycji z wpisanymi danymi — zapis kończy się wtedy ścieżką błędu
 * rodzica (toast + zamknięcie), a nie cichym przejściem w „dodawanie".
 */

import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NON_WORKING_DAY_NAME_MAX_LENGTH } from '@saas/shared';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import type { NonWorkingDayDto } from '@/lib/non-working-days/api';

import {
  type DraftEntry,
  type DraftErrors,
  hasErrors,
  NO_DRAFT_ERRORS,
  validateEntryValues,
} from './non-working-day-utils';

/**
 * Cel okna: dodawanie nowego wpisu z datą startową (kliknięty dzień / dziś) albo edycja wpisu o
 * danym id (wykluczanego z kontroli duplikatu dnia). Unia rozłączna — każde pole istnieje tylko w
 * trybie, w którym ma sens.
 */
export type NonWorkingDayModalTarget =
  | { mode: 'create'; initialDay: string }
  | { mode: 'edit'; entryId: string };

export interface NonWorkingDayFormModalProps {
  /** Cel okna zamrożony przy otwarciu — patrz nagłówek pliku. */
  target: NonWorkingDayModalTarget;
  /**
   * Aktualny edytowany wpis z listy. W trybie `edit` bywa `null`, gdy wpis zniknął z listy po
   * otwarciu okna; w trybie `create` zawsze `null`. Wartości startowe formularza liczone są tylko
   * przy montażu.
   */
  entry: NonWorkingDayDto | null;
  entries: readonly NonWorkingDayDto[];
  /**
   * Zapis wpisu. Zwraca komunikat błędu daty (kolizja z serwera) albo `null` — wtedy rodzic albo
   * zamknął okno (zapisano), albo zgłosił błąd toastem i okno zostaje z wpisanymi danymi.
   */
  onSave: (values: DraftEntry) => Promise<string | null>;
  /** Tylko edycja: prośba o usunięcie — potwierdzenie (`ConfirmDialog`) żyje w rodzicu. */
  onRequestDelete: () => void;
  onClose: () => void;
}

export function NonWorkingDayFormModal({
  target,
  entry,
  entries,
  onSave,
  onRequestDelete,
  onClose,
}: NonWorkingDayFormModalProps): ReactElement {
  const { t } = useTranslation();
  const formId = useId();
  const [values, setValues] = useState<DraftEntry>(() =>
    entry === null
      ? {
          // Edycja wpisu, którego już nie ma na liście, startuje z pustą datą (walidacja ją wyłapie).
          day: target.mode === 'create' ? target.initialDay : '',
          repeatsYearly: false,
          name: '',
        }
      : { day: entry.day, repeatsYearly: entry.repeatsYearly, name: entry.name },
  );
  const [errors, setErrors] = useState<DraftErrors>(NO_DRAFT_ERRORS);
  const [saving, setSaving] = useState(false);
  const isEdit = target.mode === 'edit';

  /** Zmiana pola czyści jego błąd; zaznaczenie „Co roku" zmienia też kryterium duplikatu dnia. */
  const patch = (next: Partial<DraftEntry>, clear: keyof DraftErrors): void => {
    setValues((prev) => ({ ...prev, ...next }));
    setErrors((prev) => (clear === 'day' ? { ...prev, day: null } : { ...prev, name: null }));
  };

  const submit = async (): Promise<void> => {
    setSaving(true);
    const message = await onSave({ ...values, name: values.name.trim() });
    setSaving(false);
    if (message !== null) setErrors((prev) => ({ ...prev, day: message }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (saving) return;
    const found = validateEntryValues(
      values,
      entries,
      t,
      target.mode === 'edit' ? target.entryId : undefined,
    );
    if (hasErrors(found)) {
      setErrors(found);
      return;
    }
    void submit();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      // W trakcie zapisu tło i Esc nie zamykają okna (zasada okien z mutacją).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      title={t(
        isEdit
          ? 'tenant.nonWorkingDays.modal.titleEdit'
          : 'tenant.nonWorkingDays.modal.titleCreate',
      )}
      testId="non-working-day-modal"
      footer={
        <>
          {isEdit && (
            <Button
              variant="danger"
              className="mr-auto"
              onClick={onRequestDelete}
              // Wpis zniknął z listy — nie ma czego usuwać (potwierdzenie nie miałoby celu).
              disabled={saving || entry === null}
              data-testid="non-working-day-modal-delete"
            >
              {t('common.delete')}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
            data-testid="non-working-day-modal-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            loading={saving}
            data-testid="non-working-day-modal-save"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormField
          label={t('tenant.nonWorkingDays.col.date')}
          required
          error={errors.day ?? undefined}
        >
          <DatePicker
            value={values.day}
            onChange={(value) => patch({ day: value }, 'day')}
            invalid={errors.day !== null}
            data-testid="non-working-day-modal-date"
          />
        </FormField>
        <Checkbox
          label={t('tenant.nonWorkingDays.col.yearly')}
          hint={t('tenant.nonWorkingDays.modal.yearlyHint')}
          checked={values.repeatsYearly}
          onChange={(e) => patch({ repeatsYearly: e.target.checked }, 'day')}
          data-testid="non-working-day-modal-yearly"
        />
        <Input
          label={t('tenant.nonWorkingDays.col.name')}
          required
          autoFocus
          maxLength={NON_WORKING_DAY_NAME_MAX_LENGTH}
          value={values.name}
          onChange={(e) => patch({ name: e.target.value }, 'name')}
          placeholder={t('tenant.nonWorkingDays.namePlaceholder')}
          error={errors.name ?? undefined}
          data-testid="non-working-day-modal-name"
        />
      </form>
    </Modal>
  );
}
